import { useState } from "react";
import {
  Crown, Phone, WhatsappLogo, ShieldCheck, ClipboardText, Strategy, ListChecks,
  Bell, GraduationCap, UsersThree, FileText, CurrencyInr, Check,
} from "@phosphor-icons/react";
import { GoogleReviews } from "../components/GoogleReviews";
import { LeadForm } from "../components/LeadForm";
import {
  useSeo, Container, PlanHero, SectionTitle, CheckItem, StepCard, FaqList, RefundBadge, money,
} from "../components/plans/PlanShared";

const ACCENT = "#6043EF";
const WA = "https://wa.me/919986143554?text=" + encodeURIComponent("Hi Unicators, I'm interested in the Priority Counselling plan. Please share the details.");

const STEPS = [
  { n: 1, title: "Profile & Assessment", body: "We study your NEET score, rank, category, budget and state preferences to build your admission blueprint." },
  { n: 2, title: "Strategy Planning", body: "A senior counsellor designs a round-wise, multi-state strategy tuned to your rank and goals." },
  { n: 3, title: "Choice Filling — Done For You", body: "We prepare and fill your preference list using live cut-off data — precise, no guesswork." },
  { n: 4, title: "Live Round Support", body: "Every round we guide you on upgrades, mop-ups and stray rounds — every deadline tracked." },
  { n: 5, title: "Seat, Fee & Reporting", body: "From allotment to fee payment and college reporting, we stay with you until admission is confirmed." },
];

const INCLUDED = [
  "Personalised rank-based planning & best-fit college suggestions",
  "Expert choice-filling done for you (AIQ + State Govt.)",
  "Dedicated personal counsellor on call & WhatsApp",
  "Complete documentation guidance & verification support",
  "Round-wise alerts for registration, choice-filling & allotments",
  "Multi-state action plan to protect your security deposit",
  "Real-time CRM tracking so no deadline is ever missed",
  "Guidance backed by Unicators' predictor, cut-off & seat-matrix data",
];

const FAQ = [
  { q: "What does Priority Counselling cover?", a: "It's an expert, done-for-you plan focused on AIQ + State Government counselling (or one State private stream). Our senior counsellors plan your strategy, fill choices, track deadlines and support you through every round until your seat is confirmed." },
  { q: "Is there an admission guarantee?", a: "No counsellor can legally guarantee a specific seat — allotments are done by MCC/State authorities on merit. We maximise your chances with an optimal choice list and flawless execution, and never let you miss a deadline." },
  { q: "When should I enroll?", a: "The earlier the better. Enrolling before results gives more time to build a strong strategy and lock the best price." },
  { q: "What if my admission involves Management/NRI quota?", a: "Those cases need our specialised NRI & Management plan. You can upgrade anytime by paying the difference." },
];

export default function PlansPriorityCounselling() {
  useSeo(
    "Priority NEET Counselling — Expert Done-For-You Admission Support | Unicators",
    "Get personalised, expert-led NEET UG counselling. Senior counsellors handle your strategy, choice-filling, documentation and round-wise tracking until your MBBS/BDS seat is secured.",
  );
  const [leadOpen, setLeadOpen] = useState(false);

  return (
    <div className="bg-white" data-testid="plans-priority-page">
      <PlanHero
        accent={ACCENT} bgFrom="#F5F3FF" bgTo="#EEF2FF"
        eyebrow="Premium · Expert-Led"
        title="From rank to seat —"
        highlight="we handle everything."
        subtitle="Stop guessing. Get personalised 1-on-1 counselling from senior experts who plan your strategy, fill your choices and track every deadline — so you focus on the outcome, not the paperwork."
        stats={[
          { value: "1-on-1", label: "Dedicated counsellor" },
          { value: "All Rounds", label: "Live support" },
          { value: "Done-for-you", label: "Choice filling" },
          { value: "10+ yrs", label: "Guidance experience" },
        ]}
      >
        <button onClick={() => setLeadOpen(true)} data-testid="hero-book-call"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-white font-bold text-sm uppercase tracking-wide shadow-[3px_3px_0px_0px_#09090B] hover:-translate-y-0.5 transition-transform" style={{ backgroundColor: ACCENT }}>
          <Phone size={16} weight="fill" /> Book a free call
        </button>
        <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#25D366] text-white font-bold text-sm uppercase tracking-wide hover:bg-[#20b957] transition-colors">
          <WhatsappLogo size={18} weight="fill" /> WhatsApp us
        </a>
      </PlanHero>

      {/* Pricing card */}
      <section className="py-16 sm:py-20">
        <Container>
          <SectionTitle center accent={ACCENT} eyebrow="Transparent pricing" title="One simple, all-inclusive plan" />
          <div className="max-w-xl mx-auto rounded-3xl border-2 border-[#6043EF] p-8 shadow-[0_20px_50px_-15px_rgba(96,67,239,0.45)] bg-white text-center" data-testid="priority-plan-card">
            <span className="w-14 h-14 rounded-2xl bg-[#6043EF]/10 flex items-center justify-center mx-auto">
              <Crown size={28} weight="fill" style={{ color: ACCENT }} />
            </span>
            <h3 className="mt-4 text-xl font-black text-[#09090B]">Priority Counselling Plan</h3>
            <p className="text-sm text-zinc-500 mt-1">Expert guidance with done-for-you execution</p>
            <div className="mt-5 flex items-end justify-center gap-3">
              <span className="text-5xl font-black text-[#09090B] font-heading">{money(25000)}</span>
              <span className="text-lg text-zinc-400 line-through mb-1.5">{money(49999)}</span>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mt-1">All-inclusive · one-time payment</p>
            <div className="mt-4 flex justify-center"><RefundBadge accent={ACCENT} text="Upgrade to full-service anytime by paying the difference" /></div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button onClick={() => setLeadOpen(true)} data-testid="priority-enroll"
                className="flex-1 h-12 rounded-full bg-[#6043EF] text-white font-bold text-sm uppercase tracking-wide flex items-center justify-center gap-2 shadow-[3px_3px_0px_0px_#09090B] hover:-translate-y-0.5 transition-transform">
                <Phone size={15} weight="fill" /> Request callback
              </button>
              <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="priority-whatsapp"
                className="flex-1 h-12 rounded-full bg-[#25D366] text-white font-bold text-sm uppercase tracking-wide flex items-center justify-center gap-2 hover:bg-[#20b957] transition-colors">
                <WhatsappLogo size={17} weight="fill" /> WhatsApp
              </a>
            </div>

            <ul className="mt-7 space-y-2.5 text-left">
              {INCLUDED.map((f, i) => (<CheckItem key={i} accent={ACCENT}>{f}</CheckItem>))}
            </ul>
          </div>
        </Container>
      </section>

      {/* Process */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-[#EEF2FF] to-white">
        <Container>
          <SectionTitle center accent={ACCENT} eyebrow="How it works"
            title="A precise, expert-led system"
            sub="Designed to maximise results and eliminate risk at every step of your counselling." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {STEPS.map((s) => (<StepCard key={s.n} {...s} accent={ACCENT} />))}
          </div>
        </Container>
      </section>

      {/* Why it stands apart */}
      <section className="py-16 sm:py-20">
        <Container>
          <SectionTitle accent={ACCENT} eyebrow="The real difference"
            title="Why our counselling stands apart"
            sub="Choosing the wrong approach can cost you a better college — or an entire year." />
          <div className="grid sm:grid-cols-2 gap-5 max-w-4xl">
            {[
              { icon: Strategy, t: "Data & Strategy", d: "Deep understanding of dynamic cut-off behaviour, rank shifts and seat changes — not just last year's numbers." },
              { icon: UsersThree, t: "One-to-One Attention", d: "Personalised handling by experienced senior counsellors, not bulk call-centre support." },
              { icon: ListChecks, t: "Complete Execution", d: "We register, fill choices and track deadlines — a true done-for-you process." },
              { icon: ShieldCheck, t: "Risk Managed", d: "CRM-based tracking means every deadline is watched, so opportunities are never missed." },
            ].map((x) => (
              <div key={x.t} className="flex items-start gap-4 bg-white rounded-2xl border border-zinc-100 p-6 shadow-sm">
                <span className="w-11 h-11 rounded-xl bg-[#6043EF]/10 flex items-center justify-center shrink-0">
                  <x.icon size={22} weight="fill" style={{ color: ACCENT }} />
                </span>
                <div>
                  <h3 className="text-base font-bold text-[#09090B]">{x.t}</h3>
                  <p className="text-sm text-[#52525B] mt-1 leading-relaxed">{x.d}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-[#F5F3FF]">
        <Container>
          <SectionTitle accent={ACCENT} eyebrow="Good to know" title="Frequently asked questions" />
          <FaqList items={FAQ} accent={ACCENT} />
        </Container>
      </section>

      <GoogleReviews />
      <LeadForm open={leadOpen} onOpenChange={setLeadOpen} plan="Priority Counselling (₹25,000)" callbackType="Priority" />
    </div>
  );
}
